import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Bot, Workflow, MessageSquare, Globe, Cpu, Zap, Database, Code, Brain } from "lucide-react";
import EditorialSteps from "@/components/EditorialSteps";
import TechnologyStack from "@/components/TechnologyStack";
import ServicesGrid from "@/components/ServicesGrid";
import CapabilityMatrix from "@/components/CapabilityMatrix";
import { usePageContent } from "@/hooks/usePageContent";
import {
  sectionVariant, heroVariant, staggerContainer, itemVariant,
  viewportOnce, glowVariant,
  problemSectionVariant, problemStaggerContainer, problemAccentLineVariant,
  problemBulletVariant, problemHeaderVariant,
} from "@/lib/motion";

const iconMap: Record<string, any> = { Bot, Workflow, MessageSquare, Globe, Cpu, Zap, Database, Code, Brain };
const resolveIcon = (name: string) => iconMap[name] || Bot;

const fallback = {
  heroLabel: "AI Automation",
  heroHeadline: "AI that works while you sleep.",
  heroDescription: "From chatbots to autonomous agents, we build AI solutions that automate workflows, engage customers, and drive efficiency at scale.",
  heroCta: "Explore AI solutions",
  useCases: [
    { title: "Lead Generation", description: "Automated lead capture and qualification" },
    { title: "Customer Support", description: "24/7 AI-powered assistance" },
    { title: "Content Creation", description: "AI-assisted content at scale" },
    { title: "Data Analysis", description: "Automated insights and reporting" },
    { title: "Personalization", description: "1:1 experiences at scale" },
    { title: "Process Automation", description: "Eliminate manual workflows" },
  ],
  problemTitle: "Why AI projects fail to deliver.",
  problems: [
    "Automation is implemented without strategic intent or ownership.",
    "AI tools exist but remain disconnected from core workflows.",
    "ROI from AI investments is unclear or unmeasured.",
    "Quick fixes create technical debt that compounds over time.",
  ],
  servicesLabel: "The System",
  servicesTitle: "AI solutions for modern businesses.",
  services: [
    { name: "Agentic AI Services", icon: "Bot", components: ["Custom AI agent development", "Multi-agent orchestration", "Autonomous workflow execution"], outcome: "AI that doesn't just assist. It autonomously executes." },
    { name: "Marketing Workflow Automation", icon: "Workflow", components: ["Lead scoring & routing", "Content personalization", "Campaign automation"], outcome: "Marketing that runs itself while you focus on strategy." },
    { name: "AI Chatbots", icon: "MessageSquare", components: ["Custom chatbot development", "Natural language understanding", "CRM & tool integration"], outcome: "Always-on engagement that scales without headcount." },
    { name: "AEO & GEO", icon: "Globe", components: ["AI search visibility audit", "Content optimization for AI", "Structured data implementation"], outcome: "Visibility in the AI-powered search landscape." },
  ],
  matrixTitle: "AI capabilities that compound.",
  matrixDescription: "This is not a starter service. These are enterprise-grade AI systems.",
  matrixCategories: [
    { id: "agents", name: "AI Agents & Automation", capabilities: [
      { id: "agentic", name: "Agentic AI Systems", icon: "Bot", description: "AI that doesn't just assist. It autonomously executes complex workflows.", outcomes: ["Autonomous task completion", "Multi-step workflow execution", "Reduced manual intervention"], decisions: ["Which workflows to automate first", "How to handle edge cases", "When to escalate to humans"], failuresPrevented: ["Bottlenecks from manual processes", "Human error in repetitive tasks", "Scaling limitations"] },
      { id: "workflows", name: "Marketing Automation", icon: "Workflow", description: "AI-powered marketing workflows that run while you sleep.", outcomes: ["Lead scoring and routing automation", "Personalized campaign triggers", "Cross-channel orchestration"], decisions: ["When to engage prospects", "What content to serve", "How to segment audiences"], failuresPrevented: ["Missed engagement windows", "Generic messaging fatigue", "Manual campaign bottlenecks"] },
      { id: "chatbots", name: "Conversational AI", icon: "MessageSquare", description: "Intelligent chatbots that understand context and intent.", outcomes: ["24/7 customer engagement", "Instant response at scale", "Qualified lead capture"], decisions: ["When to escalate to humans", "How to handle objections", "What information to collect"], failuresPrevented: ["Lost leads from slow response", "Inconsistent brand messaging", "Support team burnout"] },
    ]},
    { id: "intelligence", name: "AI Intelligence", capabilities: [
      { id: "aeo", name: "AI Search Optimization", icon: "Globe", description: "Visibility in the AI-powered search landscape (AEO/GEO).", outcomes: ["Presence in AI search results", "Structured data optimization", "Content optimized for AI consumption"], decisions: ["Which content to optimize for AI", "How to structure information", "Where to prioritize visibility"], failuresPrevented: ["Invisibility in AI-powered search", "Losing ground to AI-optimized competitors", "Missing the search paradigm shift"] },
      { id: "predictive", name: "Predictive Intelligence", icon: "Brain", description: "AI models that predict outcomes and recommend actions.", outcomes: ["Predictive lead scoring", "Churn prediction", "Next-best-action recommendations"], decisions: ["Which leads to prioritize", "When to intervene with at-risk customers", "What offers to present"], failuresPrevented: ["Wasted effort on low-quality leads", "Preventable customer churn", "Reactive instead of proactive approach"] },
    ]},
  ],
  processTitle: "From concept to autonomous execution.",
  processSteps: [
    { number: "01", title: "Assessment", description: "Evaluate your current workflows, identify automation opportunities, and define success metrics." },
    { number: "02", title: "Solution Design", description: "Architect AI solutions that integrate with your existing systems and scale with your growth." },
    { number: "03", title: "Development", description: "Build, train, and test AI agents and automation workflows with rigorous quality standards." },
    { number: "04", title: "Deployment", description: "Roll out solutions with monitoring, fallbacks, and human-in-the-loop safeguards." },
    { number: "05", title: "Optimization", description: "Continuous learning and improvement based on real-world performance data." },
  ],
  techLabel: "AI Stack",
  techTitle: "Powered by cutting-edge AI.",
  techDescription: "We leverage the most advanced AI technologies to build intelligent automation solutions.",
  techCategories: [
    { title: "LLM Providers", icon: "Cpu", items: ["OpenAI", "Anthropic", "Google AI", "Cohere"] },
    { title: "Automation", icon: "Zap", items: ["Zapier", "Make", "n8n", "Pipedream"] },
    { title: "Vector DBs", icon: "Database", items: ["Pinecone", "Weaviate", "Chroma", "Qdrant"] },
    { title: "Frameworks", icon: "Code", items: ["LangChain", "LlamaIndex", "AutoGPT", "CrewAI"] },
  ],
  ctaTitle: "Ready to automate with AI?",
  ctaDescription: "Let's explore how AI can transform your operations and drive growth.",
  ctaCta: "Start the conversation",
};

const resolveMatrixCats = (cats: any[]) => cats.map((cat) => ({ ...cat, capabilities: cat.capabilities.map((cap: any) => ({ ...cap, icon: typeof cap.icon === "string" ? resolveIcon(cap.icon) : cap.icon })) }));
const resolveTechCats = (cats: any[]) => cats.map((c) => ({ ...c, icon: typeof c.icon === "string" ? resolveIcon(c.icon) : c.icon }));
const resolveServiceIcons = (svcs: any[]) => svcs.map((s) => ({ ...s, icon: typeof s.icon === "string" ? resolveIcon(s.icon) : s.icon }));

const AIAutomation = () => {
  const { content } = usePageContent("ai-automation", fallback);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* HERO */}
      <section className="relative pt-28 pb-10 md:pt-36 md:pb-16 lg:pt-44 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <motion.div className="absolute top-0 right-1/4 w-[800px] h-[800px] rounded-full" initial="hidden" animate="visible" variants={glowVariant} style={{ background: 'radial-gradient(circle, hsl(var(--primary) / 0.06) 0%, transparent 70%)' }} />
        </div>
        <div className="container-wide relative z-10">
          <div className="max-w-3xl">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
              <motion.span variants={heroVariant} className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">{content.heroLabel}</motion.span>
              <motion.h1 variants={heroVariant} className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground mb-6 leading-[1.1]">{content.heroHeadline}</motion.h1>
              <motion.p variants={heroVariant} className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 max-w-2xl">{content.heroDescription}</motion.p>
              <motion.div variants={heroVariant} className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="group" asChild><Link to="/contact">{content.heroCta}<ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></Link></Button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="py-8 border-t border-border bg-muted/30">
        <div className="container-wide">
          <motion.div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6" initial="hidden" whileInView="visible" viewport={viewportOnce} variants={staggerContainer}>
            {content.useCases.map((uc: any) => (
              <motion.div key={uc.title} variants={itemVariant} className="text-center">
                <div className="text-sm font-medium text-foreground mb-1">{uc.title}</div>
                <div className="text-xs text-muted-foreground">{uc.description}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="py-10 md:py-16 lg:py-20 border-t border-border">
        <div className="container-wide">
        <motion.div className="max-w-3xl" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-30%" }} variants={problemSectionVariant}>
          <motion.div variants={problemHeaderVariant}>
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">The Problem</span>
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-10">{content.problemTitle}</h2>
          </motion.div>
          <motion.div className="relative" variants={problemStaggerContainer}>
            <motion.div className="absolute left-0 top-0 bottom-0 w-0.5 bg-primary/15" variants={problemAccentLineVariant} style={{ transformOrigin: "top" }} />
            <div className="space-y-5 pl-6">
              {content.problems.map((p: string, i: number) => (
                <motion.div key={i} variants={problemBulletVariant} className="group relative">
                  <div className="absolute -left-6 top-0 bottom-0 w-0.5 bg-transparent transition-colors duration-200 md:group-hover:bg-primary" />
                  <p className="text-foreground/80 transition-colors duration-200 md:group-hover:text-foreground">{p}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
        </div>
      </section>

      <ServicesGrid label={content.servicesLabel} title={content.servicesTitle} services={resolveServiceIcons(content.services)} />
      <CapabilityMatrix title={content.matrixTitle} description={content.matrixDescription} categories={resolveMatrixCats(content.matrixCategories)} />
      <EditorialSteps title={content.processTitle} steps={content.processSteps} />
      <TechnologyStack label={content.techLabel} title={content.techTitle} description={content.techDescription} categories={resolveTechCats(content.techCategories)} />

      {/* CTA */}
      <section className="py-16 md:py-24 border-t border-border bg-muted/20">
        <div className="container-wide">
          <motion.div className="max-w-2xl mx-auto text-center" initial="hidden" whileInView="visible" viewport={viewportOnce} variants={sectionVariant}>
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-4">{content.ctaTitle}</h2>
            <p className="text-muted-foreground mb-8">{content.ctaDescription}</p>
            <Button size="lg" className="group" asChild><Link to="/contact">{content.ctaCta}<ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></Link></Button>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AIAutomation;
